/**
 * TouchpointRegistryPage — Cross-Entity Touchpoint Registry (EQ-PROV-007)
 *
 * Displays and manages cross-entity touchpoints: integration points between
 * companies that trigger cross-entity dispatch.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Badge, Button, TextInput, Select, Textarea, Modal, Spinner } from 'flowbite-react';
import { HiPlus, HiPencil, HiSearch, HiTrash, HiExclamation } from 'react-icons/hi';
import { Make_Authorized_API_Call } from '@equilateral/ui-shared';
import { useStatus } from '../../contexts/StatusProvider';
import { API_ENDPOINTS } from '../../services/api/endpoints';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Touchpoint {
  touchpoint_id: string;
  touchpoint_name: string;
  description: string;
  origin_company_id: string;
  origin_company_name: string;
  target_company_id: string;
  target_company_name: string;
  typical_consequence_tier: number;
  trigger_type: 'manual' | 'scheduled' | 'event-driven' | 'threshold';
  trigger_config: Record<string, unknown> | null;
  active: boolean;
  total_dispatches: number;
  last_dispatch_at: string | null;
  avg_success_rate: number | null;
  created_at: string;
}

interface TouchpointFormData {
  touchpoint_name: string;
  description: string;
  origin_company_id: string;
  target_company_id: string;
  typical_consequence_tier: number;
  trigger_type: string;
  active: boolean;
}

type TriggerType = 'manual' | 'scheduled' | 'event-driven' | 'threshold';

interface Company {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIER_COLORS: Record<number, string> = {
  1: 'success',
  2: 'info',
  3: 'warning',
  4: 'indigo',
  5: 'failure',
};

const TIER_LABELS: Record<number, string> = {
  1: 'T1',
  2: 'T2',
  3: 'T3',
  4: 'T4',
  5: 'T5',
};

const TRIGGER_TYPES: TriggerType[] = ['manual', 'scheduled', 'event-driven', 'threshold'];

const EMPTY_FORM: TouchpointFormData = {
  touchpoint_name: '',
  description: '',
  origin_company_id: '',
  target_company_id: '',
  typical_consequence_tier: 3,
  trigger_type: 'manual',
  active: true,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRate(rate: number | null): string {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TouchpointRegistryPage: React.FC = () => {
  const { Client_ID: Selected_Client_ID } = useStatus();

  // Data state
  const [touchpoints, setTouchpoints] = useState<Touchpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterTier, setFilterTier] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTouchpoint, setEditingTouchpoint] = useState<Touchpoint | null>(null);
  const [formData, setFormData] = useState<TouchpointFormData>({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingTouchpoint, setDeletingTouchpoint] = useState<Touchpoint | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const companies: Company[] = useMemo(() => {
    const map = new Map<string, string>();
    for (const tp of touchpoints) {
      if (tp.origin_company_id) map.set(tp.origin_company_id, tp.origin_company_name);
      if (tp.target_company_id) map.set(tp.target_company_id, tp.target_company_name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [touchpoints]);

  const filteredTouchpoints = useMemo(() => {
    return touchpoints.filter((tp) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!tp.touchpoint_name.toLowerCase().includes(q)) return false;
      }
      if (filterCompany) {
        if (tp.origin_company_id !== filterCompany && tp.target_company_id !== filterCompany) {
          return false;
        }
      }
      if (filterTier) {
        if (tp.typical_consequence_tier !== Number(filterTier)) return false;
      }
      return true;
    });
  }, [touchpoints, searchQuery, filterCompany, filterTier]);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------

  const fetchTouchpoints = useCallback(async () => {
    if (!Selected_Client_ID) return;
    setLoading(true);
    setError('');
    try {
      const url = `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_TOUCHPOINTS_GET}?Client_ID=${encodeURIComponent(Selected_Client_ID)}`;
      const response = await Make_Authorized_API_Call(url, 'GET');
      if (!response?.success) {
        throw new Error((response as any)?.error_details || 'Failed to fetch touchpoints');
      }
      const records: Touchpoint[] = response.data?.Records ?? [];
      setTouchpoints(records);
      setTotalCount(response.meta?.Total_Records ?? records.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load touchpoints');
    } finally {
      setLoading(false);
    }
  }, [Selected_Client_ID]);

  useEffect(() => {
    fetchTouchpoints();
  }, [fetchTouchpoints]);

  // ---------------------------------------------------------------------------
  // Modal handlers
  // ---------------------------------------------------------------------------

  const openCreateModal = () => {
    setEditingTouchpoint(null);
    setFormData({ ...EMPTY_FORM });
    setSubmitError('');
    setModalOpen(true);
  };

  const openEditModal = (tp: Touchpoint) => {
    setEditingTouchpoint(tp);
    setFormData({
      touchpoint_name: tp.touchpoint_name,
      description: tp.description,
      origin_company_id: tp.origin_company_id,
      target_company_id: tp.target_company_id,
      typical_consequence_tier: tp.typical_consequence_tier,
      trigger_type: tp.trigger_type,
      active: tp.active,
    });
    setSubmitError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTouchpoint(null);
    setSubmitError('');
  };

  const handleSubmit = async () => {
    if (!formData.touchpoint_name.trim()) {
      setSubmitError('Touchpoint name is required.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      const isEdit = editingTouchpoint !== null;
      const url = isEdit
        ? API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_TOUCHPOINT_UPDATE
        : API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_TOUCHPOINT_CREATE;
      const method = isEdit ? 'PUT' : 'POST';

      const body: Record<string, unknown> = {
        touchpoint_name: formData.touchpoint_name,
        description: formData.description,
        origin_company_id: formData.origin_company_id,
        target_company_id: formData.target_company_id,
        typical_consequence_tier: Number(formData.typical_consequence_tier),
        trigger_type: formData.trigger_type,
        active: formData.active,
      };

      if (isEdit) {
        body.touchpoint_id = editingTouchpoint.touchpoint_id;
      }

      const response = await Make_Authorized_API_Call(url, method, body);
      if (!response?.success) {
        throw new Error((response as any)?.error_details || `Failed to ${isEdit ? 'update' : 'create'} touchpoint`);
      }

      closeModal();
      await fetchTouchpoints();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field: keyof TouchpointFormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------------------------------------------------------------------------
  // Delete handlers
  // ---------------------------------------------------------------------------

  const openDeleteModal = (tp: Touchpoint) => {
    setDeletingTouchpoint(tp);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletingTouchpoint(null);
  };

  const handleDelete = async () => {
    if (!deletingTouchpoint) return;
    setDeleting(true);
    try {
      const url = `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_TOUCHPOINT_UPDATE}`;
      const response = await Make_Authorized_API_Call(url, 'DELETE', {
        touchpoint_id: deletingTouchpoint.touchpoint_id,
      });
      if (!response?.success) {
        throw new Error((response as any)?.error_details || 'Failed to delete touchpoint');
      }
      closeDeleteModal();
      await fetchTouchpoints();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete touchpoint');
      closeDeleteModal();
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Cross-Entity Touchpoint Registry
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {totalCount} touchpoint{totalCount !== 1 ? 's' : ''} registered
          </p>
        </div>
        <Button color="blue" onClick={openCreateModal}>
          <HiPlus className="mr-2 h-4 w-4" />
          Create Touchpoint
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <TextInput
            icon={HiSearch}
            placeholder="Search by touchpoint name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
            <option value="">All Companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-full sm:w-36">
          <Select value={filterTier} onChange={(e) => setFilterTier(e.target.value)}>
            <option value="">All Tiers</option>
            {[1, 2, 3, 4, 5].map((t) => (
              <option key={t} value={t}>
                Tier {t}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center py-16">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-500">Loading touchpoints...</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filteredTouchpoints.length === 0 && (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <p className="text-lg font-medium mb-2">No touchpoints found</p>
          <p className="text-sm">
            {touchpoints.length === 0
              ? 'Create your first cross-entity touchpoint to get started.'
              : 'Try adjusting your filters.'}
          </p>
        </div>
      )}

      {/* Touchpoint grid */}
      {!loading && filteredTouchpoints.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredTouchpoints.map((tp) => (
            <Card key={tp.touchpoint_id} className="relative">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                  {tp.touchpoint_name}
                </h3>
                <div className="flex gap-1">
                  <Button size="xs" color="light" onClick={() => openEditModal(tp)}>
                    <HiPencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="xs" color="failure" onClick={() => openDeleteModal(tp)}>
                    <HiTrash className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Origin -> Target */}
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                <span className="font-medium">{tp.origin_company_name}</span>
                <span className="mx-2 text-gray-400">&rarr;</span>
                <span className="font-medium">{tp.target_company_name}</span>
              </p>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge color={TIER_COLORS[tp.typical_consequence_tier] ?? 'gray'}>
                  {TIER_LABELS[tp.typical_consequence_tier] ?? `T${tp.typical_consequence_tier}`}
                </Badge>
                <Badge color="purple">{tp.trigger_type}</Badge>
                <Badge color={tp.active ? 'success' : 'gray'}>
                  {tp.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 mt-4 text-xs text-gray-500 dark:text-gray-400">
                <div>
                  <span className="block font-semibold text-gray-700 dark:text-gray-300">
                    {tp.total_dispatches.toLocaleString()}
                  </span>
                  Dispatches
                </div>
                <div>
                  <span className="block font-semibold text-gray-700 dark:text-gray-300">
                    {formatRate(tp.avg_success_rate)}
                  </span>
                  Success Rate
                </div>
                <div>
                  <span className="block font-semibold text-gray-700 dark:text-gray-300">
                    {formatDate(tp.last_dispatch_at)}
                  </span>
                  Last Dispatch
                </div>
              </div>

              {/* Description */}
              {tp.description && (
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                  {tp.description}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal show={modalOpen} onClose={closeModal} size="lg">
        <Modal.Header>
          {editingTouchpoint ? 'Edit Touchpoint' : 'Create Touchpoint'}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {submitError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
                {submitError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Touchpoint Name
              </label>
              <TextInput
                value={formData.touchpoint_name}
                onChange={(e) => updateField('touchpoint_name', e.target.value)}
                placeholder="e.g. Invoice Data Sync"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe this touchpoint..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Origin Company
                </label>
                <Select
                  value={formData.origin_company_id}
                  onChange={(e) => updateField('origin_company_id', e.target.value)}
                >
                  <option value="">
                    {companies.length === 0 ? 'No companies available' : 'Select origin company'}
                  </option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                {companies.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Companies will appear once touchpoints exist.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Target Company
                </label>
                <Select
                  value={formData.target_company_id}
                  onChange={(e) => updateField('target_company_id', e.target.value)}
                >
                  <option value="">
                    {companies.length === 0 ? 'No companies available' : 'Select target company'}
                  </option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                {companies.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    Companies will appear once touchpoints exist.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Consequence Tier
                </label>
                <Select
                  value={formData.typical_consequence_tier}
                  onChange={(e) => updateField('typical_consequence_tier', Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5].map((t) => (
                    <option key={t} value={t}>
                      Tier {t}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Trigger Type
                </label>
                <Select
                  value={formData.trigger_type}
                  onChange={(e) => updateField('trigger_type', e.target.value)}
                >
                  {TRIGGER_TYPES.map((tt) => (
                    <option key={tt} value={tt}>
                      {tt.charAt(0).toUpperCase() + tt.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="touchpoint-active"
                checked={formData.active}
                onChange={(e) => updateField('active', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor="touchpoint-active"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Active
              </label>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3 w-full">
            <Button color="gray" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button color="blue" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {editingTouchpoint ? 'Updating...' : 'Creating...'}
                </>
              ) : editingTouchpoint ? (
                'Update Touchpoint'
              ) : (
                'Create Touchpoint'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModalOpen} onClose={closeDeleteModal} size="md" popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiExclamation className="mx-auto mb-4 h-14 w-14 text-red-400" />
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
              Delete Touchpoint
            </h3>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              Are you sure you want to delete{' '}
              <span className="font-medium">{deletingTouchpoint?.touchpoint_name}</span>? This
              action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, delete'}
              </Button>
              <Button color="gray" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TouchpointRegistryPage;
